import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import MarketingLayout from '@/components/marketing/MarketingLayout';
import { ArrowRight, ShieldCheck, Sparkles, Check } from '@/components/icons';

const FEATURES = [
  { img: '/assets/klaro/icons/ddt-ocr.png', title: 'OCR DDT con AI', desc: "Fotografi il DDT: l'intelligenza artificiale legge righe, lotti e scadenze e crea i lotti in magazzino." },
  { img: '/assets/klaro/icons/warehouse.png', title: 'Magazzino tracciato', desc: 'Ogni ingrediente ha un lotto univoco: sai sempre da quale fornitore arriva e dov\'è finito.' },
  { img: '/assets/klaro/icons/recipes.png', title: 'Ricette + Batch', desc: 'Componi ricette, registra produzioni e genera automaticamente i lotti di semilavorato o prodotto finito.' },
  { img: '/assets/klaro/icons/labels.png', title: 'Etichette pronte', desc: 'Template drag & drop conformi HACCP, stampa PDF o direttamente su etichettatrice.' },
];

const STEPS = [
  { n: 1, title: 'Carica DDT', desc: 'Foto o PDF: Klaro legge tutto in pochi secondi.' },
  { n: 2, title: 'AI crea lotti', desc: 'Ingredienti, scadenze e fornitori registrati in automatico.' },
  { n: 3, title: 'Ricetta + batch', desc: 'Trasforma i lotti in ricette e produzioni tracciate.' },
  { n: 4, title: 'Etichetta + HACCP', desc: 'Stampa etichette conformi e archivia il registro.' },
];

export default function HomePage() {
  return (
    <MarketingLayout>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-blue-100/60 blur-3xl" />
          <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-emerald-100/60 blur-3xl" />
        </div>
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 pt-14 pb-16 sm:pt-20 sm:pb-24 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 border border-blue-200 px-3 py-1 text-xs font-semibold text-[#1D4ED8]">
              <Sparkles className="w-3.5 h-3.5" />Piattaforma HACCP
            </div>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#0A1737] leading-[1.05] tracking-tight">
              Basta carta.<br />
              Klaro digitalizza il tuo laboratorio <span className="text-[#2563EB]">in un pomeriggio.</span>
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-xl">DDT letti dall'AI, lotti tracciati, ricette, batch di produzione ed etichette PDF pronte. Tutto in un unico strumento pensato per piccoli laboratori alimentari.</p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Button asChild variant="success" size="lg" className="h-12 text-base"><Link to="/register" data-testid="hero-register-btn">Registra la tua azienda<ArrowRight className="w-5 h-5 ml-1" /></Link></Button>
              <Button asChild variant="outline" size="lg" className="h-12 text-base"><Link to="/come-funziona" data-testid="hero-how-btn">Scopri come funziona</Link></Button>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" />GDPR compliant</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" />Nessuna carta di credito</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" />Setup in 15 minuti</span>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-10 bg-gradient-to-br from-blue-500/10 via-transparent to-emerald-500/10 rounded-[3rem] blur-3xl" aria-hidden="true" />
            <div className="relative mx-auto max-w-[520px]">
              <div className="relative rounded-[2.25rem] border-[12px] border-slate-900 bg-slate-900 shadow-2xl">
                <div className="overflow-hidden rounded-[1.25rem] bg-white aspect-[16/10]">
                  <img src="/brand/dashboard-preview.png" alt="Dashboard Klaro su tablet" className="block w-full h-full object-contain" loading="lazy" />
                </div>
                <span aria-hidden="true" className="absolute top-1/2 -translate-y-1/2 left-[-6px] h-2 w-2 rounded-full bg-slate-600"></span>
              </div>
              <div className="absolute -bottom-5 -right-3 sm:-right-6 flex items-center gap-2 rounded-xl bg-emerald-600 text-white px-4 py-2.5 shadow-lg text-sm font-semibold">
                <ShieldCheck className="w-5 h-5" />HACCP conforme
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-16">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-wide font-semibold text-[#2563EB]">Cosa fa Klaro</p>
          <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-semibold text-[#0A1737]">Quattro strumenti, un'unica app.</h2>
          <p className="mt-3 text-slate-600">Sostituisce quaderni, Excel e stampanti scollegate. Progettato per essere usato su tablet in laboratorio.</p>
        </div>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f) => (
            <div key={f.title} className="group rounded-2xl border border-slate-200 bg-white p-6 hover:border-blue-300 hover:shadow-md transition-all" data-testid={`feature-card-${f.title.split(' ')[0].toLowerCase()}`}>
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-slate-50 group-hover:bg-blue-50 transition-colors">
                <img src={f.img} alt="" aria-hidden="true" className="h-10 w-10 object-contain" loading="lazy" />
              </span>
              <h3 className="mt-4 font-heading text-lg font-semibold text-[#0A1737]">{f.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* STEPS PREVIEW */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-16">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-wide font-semibold text-[#2563EB]">Il flusso</p>
            <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-semibold text-[#0A1737]">Da DDT a etichetta in 4 passi.</h2>
          </div>
          <ol className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s) => (
              <li key={s.n} className="rounded-2xl bg-white border border-slate-200 p-6">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[#2563EB] text-white font-heading font-bold">{s.n}</span>
                <h3 className="mt-4 font-heading text-lg font-semibold text-[#0A1737]">{s.title}</h3>
                <p className="mt-1 text-sm text-slate-600">{s.desc}</p>
              </li>
            ))}
          </ol>
          <div className="mt-8"><Button asChild variant="outline"><Link to="/come-funziona">Vedi il flusso completo<ArrowRight className="w-4 h-4 ml-1" /></Link></Button></div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-20">
        <div className="relative overflow-hidden rounded-3xl bg-[#0A1737] text-white p-10 sm:p-16">
          <div className="absolute -top-32 -right-32 h-72 w-72 rounded-full bg-emerald-500/20 blur-3xl" aria-hidden="true" />
          <div className="relative">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold max-w-2xl">Pronto a lasciare la carta in un cassetto?</h2>
            <p className="mt-3 text-white/80 max-w-xl">Registrati oggi, attiviamo la tua azienda entro poche ore. Nessuna carta di credito richiesta.</p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Button asChild variant="success" size="lg" className="h-12 text-base"><Link to="/register">Inizia gratis<ArrowRight className="w-5 h-5 ml-1" /></Link></Button>
              <Button asChild size="lg" className="h-12 text-base bg-white/10 hover:bg-white/20 text-white border border-white/20"><Link to="/prezzi">Vedi i piani</Link></Button>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
